<template>
  <div class="translations-page">
    <translations-table
      :itens="composable.itens.value"
      :idiomas="composable.idiomas.value"
      :paginas="composable.paginas.value"
      :pagina="pagina"
      :idioma="idioma"
      :busca="busca"
      :modo="modo"
      :faltantes="faltantes"
      :total="composable.total.value"
      :mais="composable.mais.value"
      :loading="composable.loading.value"
      :save-error="saveError"
      @update:pagina="onPaginaChange"
      @update:idioma="onIdiomaChange"
      @update:busca="(v: string) => (busca = v)"
      @update:modo="(v: BuscaModo) => (modo = v)"
      @update:faltantes="onFaltantesChange"
      @search="runSearch"
      @refresh="loadList"
      @add-line="openModal"
      @retranslate="confirmRetranslatePage"
      @save-translation="saveTranslation"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../eventBus'
import { log } from '../../utils/DebugLogger'
import {
  useTranslationsAdmin,
  type BuscaModo,
} from '../../composables/useTranslationsAdmin'
import TranslationsTable from './TranslationsTable.vue'

const { t } = useI18n()
const composable = useTranslationsAdmin()

const pagina = ref<string>('')
const idioma = ref<string>('')
const busca = ref<string>('')
const modo = ref<BuscaModo>('padrao')
const faltantes = ref<boolean>(false)
const saveError = ref<string | null>(null)

/**
 * A page-less `lista` makes the proxy read the whole upstream database, so the
 * panel always carries one: the first page of the catalog is selected on mount
 * and the list only ever runs with a page in hand.
 */
const loadList = async (): Promise<void> => {
  if (!pagina.value) return
  try {
    await composable.listar({
      pagina: pagina.value,
      idioma: idioma.value,
      faltantes: faltantes.value,
    })
  } catch (err) {
    log('TranslationsPage', 'loadList', 'Erro', err)
  }
}

/** The search box: empty query falls back to the plain page listing. */
const runSearch = async (): Promise<void> => {
  saveError.value = null
  if (!busca.value.trim()) {
    await loadList()
    return
  }
  try {
    await composable.buscar({
      q: busca.value.trim(),
      modo: modo.value,
      idioma: idioma.value,
      pagina: pagina.value,
    })
  } catch (err) {
    log('TranslationsPage', 'runSearch', 'Erro', err)
  }
}

const onPaginaChange = async (v: string): Promise<void> => {
  if (v === pagina.value) return
  pagina.value = v
  await loadList()
}

const onIdiomaChange = async (v: string): Promise<void> => {
  if (v === idioma.value) return
  idioma.value = v
  await loadList()
}

const onFaltantesChange = async (v: boolean): Promise<void> => {
  faltantes.value = v
  await loadList()
}

/**
 * TranslationsModal is mounted once globally by ModalManager, so the page opens
 * it over the eventBus and hands it the catalog it needs — the modal has no
 * composable of its own to fetch pages with.
 */
const openModal = (): void => {
  eventBus.emit('modal.translationsAdmin.open', {
    paginas: composable.paginas.value,
    pagina: pagina.value,
  })
}

/**
 * One save = one key, one language. The blank-text refusal is kept on screen
 * (the proxy did NOT write) instead of being toasted away.
 */
const saveTranslation = async (payload: {
  pagina: string
  chave: string
  texto: string
}): Promise<void> => {
  saveError.value = null
  try {
    await composable.salvar({
      pagina: payload.pagina,
      chave: payload.chave,
      idioma: idioma.value,
      texto: payload.texto,
    })
  } catch (err) {
    saveError.value = err instanceof Error ? err.message : String(err)
    log('TranslationsPage', 'saveTranslation', 'Erro', saveError.value)
  }
}

/**
 * Retranslating a whole page is one machine-translation call per key per
 * language, so it is confirmed before it is fired.
 */
const confirmRetranslatePage = async (): Promise<void> => {
  if (!pagina.value) return
  const ok = window.confirm(
    t('Retraduzir TODAS as chaves da página {pagina}? Isso dispara uma tradução automática por chave e idioma.', {
      pagina: pagina.value,
    }),
  )
  if (!ok) return
  try {
    await composable.retraduzir({ pagina: pagina.value })
    await loadList()
  } catch (err) {
    log('TranslationsPage', 'confirmRetranslatePage', 'Erro', err)
  }
}

const onCreated = async (): Promise<void> => {
  await loadList()
}

onMounted(async () => {
  try {
    await composable.carregarCatalogo()
  } catch (err) {
    log('TranslationsPage', 'onMounted', 'Erro ao carregar catálogo', err)
    return
  }
  if (!pagina.value) {
    pagina.value = composable.paginas.value[0]?.nome ?? ''
  }
  await loadList()
  eventBus.on('translationsAdmin.created', onCreated)
})

onBeforeUnmount(() => {
  eventBus.off('translationsAdmin.created', onCreated)
})
</script>

<style scoped>
.translations-page {
  padding: 20px;
}
</style>
